import { type SidebarFolderHandlers, type SidebarNavSpaceFolder, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  createSidebarSectionMutation,
  deleteSidebarSectionMutation,
  type GetSpaceSidebarResponse,
  getSpaceSidebarOptions,
  getSpaceSidebarQueryKey,
  moveSidebarSpaceMutation,
  reorderSidebarSectionsMutation,
  type SidebarSectionResponse,
  updateSidebarSectionMutation,
} from "@/api";
import {
  deleteFolderLocally,
  generalKey,
  moveSpaceLocally,
  reorderFoldersLocally,
  toNavFolders,
  updateFolderLocally,
  visibleOnly,
} from "./sidebarFolders";

/**
 * The user's own sidebar folders (docs/design/web/WEB_SPACE_SECTIONS_PLAN.md; the API calls them sections).
 * Changes show at once (optimistic) and are replaced by the server's answer: every write returns the whole
 * sidebar. On failure: a toast and a refetch.
 */
export function useSidebarFolders(spaceKeys: string[]): {
  folders: SidebarNavSpaceFolder[] | undefined;
  handlers: SidebarFolderHandlers;
} {
  const { t } = useTranslation("shell", { keyPrefix: "sidebar.folders" });
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const key = getSpaceSidebarQueryKey();
  const query = useQuery(getSpaceSidebarOptions());

  const current = () => queryClient.getQueryData<GetSpaceSidebarResponse>(key)?.data.sections ?? [];
  const setSections = (sections: SidebarSectionResponse[]) =>
    queryClient.setQueryData<GetSpaceSidebarResponse>(key, (old) => old && { ...old, data: { ...old.data, sections } });
  // Shown at once; a refetch in flight would overwrite it, so it's cancelled.
  const optimistic = (change: (sections: SidebarSectionResponse[]) => SidebarSectionResponse[]) => {
    void queryClient.cancelQueries({ queryKey: key });
    setSections(change(current()));
  };
  const settle = {
    onSuccess: (response: GetSpaceSidebarResponse) => queryClient.setQueryData(key, response),
    onError: () => {
      toast.error(t("saveFailed"));
      queryClient.invalidateQueries({ queryKey: key });
    },
  };

  const create = useMutation({ ...createSidebarSectionMutation(), ...settle });
  const update = useMutation({ ...updateSidebarSectionMutation(), ...settle });
  const remove = useMutation({ ...deleteSidebarSectionMutation(), ...settle });
  const reorder = useMutation({ ...reorderSidebarSectionsMutation(), ...settle });
  const move = useMutation({ ...moveSidebarSpaceMutation(), ...settle });

  const handlers: SidebarFolderHandlers = {
    onCreateFolder: (name) => create.mutate({ body: { name } }),
    onCreateFolderWithSpace: (name, spaceKey) => {
      const before = new Set(current().map((s) => s.uuid));
      create.mutate(
        { body: { name } },
        {
          onSuccess: (response) => {
            const added = response.data.sections?.find((s) => !before.has(s.uuid));
            if (added) move.mutate({ path: { space_uuid: spaceKey }, body: { sectionUuid: added.uuid } });
          },
        },
      );
    },
    onRenameFolder: (folderKey, name) => {
      optimistic((s) => updateFolderLocally(s, folderKey, { name }));
      update.mutate({ path: { section_uuid: folderKey }, body: { name } });
    },
    onDeleteFolder: (folderKey) => {
      optimistic((s) => deleteFolderLocally(s, folderKey));
      remove.mutate({ path: { section_uuid: folderKey } });
    },
    onSetFolderSort: (folderKey, sortMode) => {
      optimistic((s) => updateFolderLocally(s, folderKey, { sortMode }));
      update.mutate({ path: { section_uuid: folderKey }, body: { sortMode } });
    },
    onToggleFolderCollapsed: (folderKey, collapsed) => {
      optimistic((s) => updateFolderLocally(s, folderKey, { collapsed }));
      update.mutate({ path: { section_uuid: folderKey }, body: { collapsed } });
    },
    onReorderFolders: (folderKeys) => {
      optimistic((s) => reorderFoldersLocally(s, folderKeys));
      reorder.mutate({ body: { sectionUuids: folderKeys } });
    },
    onMoveSpace: (spaceKey, folderKey, index) => {
      optimistic((s) => moveSpaceLocally(s, spaceKey, folderKey, index));
      move.mutate({ path: { space_uuid: spaceKey }, body: { sectionUuid: folderKey, position: index } });
    },
    onRemoveFromFolder: (spaceKey) => {
      optimistic((s) => {
        const general = generalKey(s);
        return general ? moveSpaceLocally(s, spaceKey, general, null) : s;
      });
      move.mutate({ path: { space_uuid: spaceKey }, body: { sectionUuid: null } });
    },
    onOpenSpaceSettings: (spaceKey) => navigate(`/spaces/${spaceKey}/settings`),
  };

  const raw = query.data?.data.sections;
  const folders = useMemo(() => (raw ? visibleOnly(toNavFolders(raw), spaceKeys) : undefined), [raw, spaceKeys]);
  // If the folders can't load, the spaces still show (all outside folders) rather than "All spaces" going missing.
  return { folders: query.isError ? [] : folders, handlers };
}
